/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/ScopePricelistDrawer.tsx
 * FILE_ID: QB-AGGRID-PRICELIST-DRAWER-001
 * PURPOSE: Split-screen (NOT overlay) pricelist panel docked to the right of the
 *          quote scope grid. Renders the full editable @quotech/pricelist
 *          workbook (browse + edit/structure inline) and a "→ add to quote"
 *          action on every row that prefills the focused/pad quote line.
 * WHY: Operators asked to structure + reference the pricelist alongside the
 *      quote, side-by-side, instead of the old modal overlay that hid the grid.
 * FLOW: getApiClient → createPricelistClient → resolve default pricebook →
 *       PricelistWorkbook(onPickItem) → onPickRow → grid prefill.
 * RELATED: ScopeAgGridSection (host), usePricelistPicker (handleSelect),
 *          mapPickedItemToPriceBookItem (PickedItem→item), @quotech/pricelist
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { X, Loader2 } from 'lucide-react'
import { PricelistWorkbook, createPricelistClient } from '@quotech/pricelist'
import { createPricelistIqClient, usePricelistIq } from '@quotech/quote-builder'
import type { PickedItem, PriceBookSummary } from '@quotech/pricelist'
import { getApiClient } from '@/lib/setupApi'

const MIN_W = 340
const MAX_W = 760
const WIDTH_KEY = 'quotech:qb-pricelist-drawer-width'
const clampW = (px: number) => Math.min(MAX_W, Math.max(MIN_W, px))

export interface ScopePricelistDrawerProps {
  onClose: () => void
  /** Fires with the resolved picked item; host maps + prefills the quote line. */
  onPickRow: (picked: PickedItem) => void
}

export default function ScopePricelistDrawer({ onClose, onPickRow }: ScopePricelistDrawerProps) {
  // getApiClient() baseURL ends in `/api`, so pass `/quotebuilder` (NOT the
  // package default `/api/quotebuilder`) — same as the Quote Hub WorkbookTab.
  const client = useMemo(() => createPricelistClient(getApiClient(), '/quotebuilder'), [])
  const iqClient = useMemo(() => createPricelistIqClient(getApiClient()), [])
  const iq = usePricelistIq(iqClient)
  const [width, setWidth] = useState(() => {
    if (typeof window === 'undefined') return 460
    const saved = Number(window.localStorage.getItem(WIDTH_KEY))
    return Number.isFinite(saved) && saved > 0 ? clampW(saved) : 460
  })
  const [pricebookId, setPricebookId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    try {
      window.localStorage.setItem(WIDTH_KEY, String(width))
    } catch {
      /* private mode / quota — width still works this session */
    }
  }, [width])

  useEffect(() => {
    let alive = true
    client
      .listPricebooks()
      .then((books: PriceBookSummary[]) => {
        if (!alive) return
        setPricebookId(books.find((b) => b.is_default)?.id ?? books[0]?.id ?? null)
        setLoading(false)
      })
      .catch(() => {
        if (alive) {
          setError('Could not load the pricelist.')
          setLoading(false)
        }
      })
    return () => {
      alive = false
    }
  }, [client])

  const onResizeDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      const startX = e.clientX
      const startWidth = width
      const move = (ev: MouseEvent) => setWidth(clampW(startWidth + (startX - ev.clientX)))
      const up = () => {
        document.removeEventListener('mousemove', move)
        document.removeEventListener('mouseup', up)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
      document.addEventListener('mousemove', move)
      document.addEventListener('mouseup', up)
    },
    [width],
  )

  return (
    <aside
      className="relative flex h-full min-h-0 flex-col border-l border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900"
      style={{ width, flex: '0 0 auto' }}
    >
      <div
        onMouseDown={onResizeDown}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize pricelist panel"
        className="absolute left-0 top-0 z-10 h-full w-1.5 cursor-col-resize hover:bg-emerald-400/40"
      />
      <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2 dark:border-slate-700">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Pricelist</h3>
        <button
          onClick={onClose}
          aria-label="Close pricelist"
          className="flex h-6 w-6 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto p-2">
        {loading ? (
          <div className="flex h-32 items-center justify-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading pricelist…
          </div>
        ) : error ? (
          <p className="p-4 text-sm text-red-600">{error}</p>
        ) : pricebookId ? (
          <PricelistWorkbook
            client={client}
            pricebookId={pricebookId}
            onPickItem={onPickRow}
            iqEnabled={iq.enabled}
          />
        ) : (
          <p className="p-4 text-sm text-slate-500">No pricelist found for this organisation.</p>
        )}
      </div>
    </aside>
  )
}
